import {Type} from '@wandb/weave/core';
import {typedDictPropertyTypes} from '@wandb/weave/core/model/helpers';
import {wandbJsonType} from '@wandb/weave/core/ops/domain/run';
import {produce} from 'immer';
import {useCallback, useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import {
  ArtifactVersionsTableDataQueryHookResult,
  useArtifactCollectionOverviewQuery,
  useArtifactTagsQuery,
  useArtifactVersionsTableDataQuery,
} from '../../generated/graphql';
import {propagateErrorsContext} from '../../util/errors';
import {mapGQLTags, ModifiedTag} from '../TagAssignment';
import {isSystemMetric} from './../../util/metrics';

type CreatedByUser = {
  type: 'User';
  id: string;
  name: string;
  username?: string;
  photoUrl?: string;
};

type CreatedByRun = {
  type: 'Run';
  id: string;
  name: string;
  displayName?: string;
  defaultColorIndex?: number;
  projectName?: string;
  entityName?: string;
};

export type ArtifactVersionsTableData = {
  id: string;
  versionIndex: number;
  createdBy?: CreatedByUser | CreatedByRun;
  tags: ModifiedTag[];
  metadata: Record<string, any>;
  ttlDurationSeconds: number;
  usedCount: number;
  size: number;
  aliases: string[];
  isGenerated: boolean;
  runHistory: Record<string, any>;
  createdAt?: string;
};

function parseArtifactVersionsTableData(
  data: ArtifactVersionsTableDataQueryHookResult['data']
): {
  tableData: ArtifactVersionsTableData[];
  historyKeys: Set<string>;
  runHistoryError: boolean;
  metadataError: boolean;
} {
  const historyKeys = new Set<string>();
  let runHistoryError = false;
  let metadataError = false;
  const membershipEdges = data?.artifactCollection?.artifactMemberships.edges;
  if (!membershipEdges) {
    return {
      tableData: [],
      historyKeys,
      runHistoryError: false,
      metadataError: false,
    };
  }
  const tableData = membershipEdges.map(edge => {
    let runHistory: Record<string, any> = {};
    const createdBy = edge.node?.artifact?.createdBy;
    let createdByUserOrRun: CreatedByUser | CreatedByRun | undefined;
    if (createdBy && createdBy.__typename === 'User') {
      const createdByUser: CreatedByUser = {
        type: 'User',
        id: createdBy.id,
        name: createdBy.name,
        username: createdBy.username ?? undefined,
        photoUrl: createdBy.photoUrl ?? undefined,
      };
      createdByUserOrRun = createdByUser;
    } else if (createdBy && createdBy.__typename === 'Run') {
      const createdByRun: CreatedByRun = {
        type: 'Run',
        id: createdBy.id,
        name: createdBy.name,
        displayName: createdBy.displayName ?? undefined,
        defaultColorIndex: createdBy.defaultColorIndex ?? undefined,
        projectName: createdBy.project?.name ?? undefined,
        entityName: createdBy.project?.entityName ?? undefined,
      };
      createdByUserOrRun = createdByRun;
      const runHistoryString = edge.node?.artifact?.runHistory;
      if (runHistoryString) {
        try {
          runHistory = JSON.parse(runHistoryString);
          const numericalHistoryKeys = getNumericalHistoryKeys(runHistory);
          numericalHistoryKeys.forEach(key => historyKeys.add(key));
        } catch (e) {
          console.error({e});
          runHistoryError = true;
        }
      }
    }

    let parsedMetadata: Record<string, any> = {};
    try {
      if (edge.node?.artifact?.metadata) {
        parsedMetadata = JSON.parse(edge.node?.artifact?.metadata);
        parsedMetadata = filterMetadataKeys(parsedMetadata);
      }
    } catch (e) {
      console.error({e});
      metadataError = true;
    }

    return {
      id: edge.node?.artifact?.id ?? '',
      versionIndex: edge.node?.versionIndex ?? 0,
      createdBy: createdByUserOrRun,
      tags: edge.node?.artifact?.tags?.map(tag => tag as ModifiedTag) ?? [],
      metadata: parsedMetadata,
      ttlDurationSeconds: edge.node?.artifact?.ttlDurationSeconds ?? 0,
      usedCount: edge.node?.artifact?.usedCount ?? 0,
      size: edge.node?.artifact?.size ?? 0,
      aliases: edge.node?.aliases.map(alias => alias.alias) ?? [],
      isGenerated: edge.node?.artifact?.isGenerated ?? false,
      createdAt: edge.node?.artifact?.createdAt ?? undefined,
      runHistory,
    };
  });
  return {tableData, historyKeys, runHistoryError, metadataError};
}

type ArtifactVersionsHookProps = {
  artifactCollectionID: string;
  initialPageSize: number;
};

export type PaginatedArtifactVersionsTableDataResult = {
  data: ArtifactVersionsTableData[];
  loading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  setPageSize: (pageSize: number) => void;
  pageSize: number;
  // In order to render the metadata as weave panels in the table,
  // we need to know the weave type of each key in the metadata. This
  // is computed from the latest version of the artifact.
  metadataKeyTypes: Record<string, Type>;
  historyKeys: string[];
  refetch: () => void;
};

export const usePaginatedArtifactVersionsTableData = ({
  artifactCollectionID,
  initialPageSize,
}: ArtifactVersionsHookProps): PaginatedArtifactVersionsTableDataResult => {
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [artifactVersionsTableData, setArtifactVersionsTableData] = useState<
    ArtifactVersionsTableData[]
  >([]);
  const [historyKeys, setHistoryKeys] = useState<Set<string>>(
    new Set<string>()
  );

  const {fetchMore, data, loading, refetch} = useArtifactVersionsTableDataQuery(
    {
      variables: {
        artifactCollectionID,
        limit: pageSize,
      },
      context: propagateErrorsContext(),
      notifyOnNetworkStatusChange: true,
      onCompleted: data => {
        const {tableData, historyKeys, runHistoryError, metadataError} =
          parseArtifactVersionsTableData(data);
        setArtifactVersionsTableData(tableData);
        setHistoryKeys(prev => new Set([...prev, ...historyKeys]));
        if (runHistoryError) {
          toast.error(
            'Something went wrong parsing run history for some artifact versions, see console for details.'
          );
        }
        if (metadataError) {
          toast.error(
            'Something went wrong parsing metadata for some artifact versions, see console for details.'
          );
        }
      },
    }
  );

  const fetchNextPage = useCallback(() => {
    const cursor =
      data?.artifactCollection?.artifactMemberships.pageInfo.endCursor;
    fetchMore({
      variables: {
        artifactCollectionID,
        limit: pageSize,
        after: cursor,
      },
      updateQuery: (prev: any, {fetchMoreResult}: {fetchMoreResult?: any}) => {
        const searchResult =
          fetchMoreResult?.artifactCollection?.artifactMemberships;
        if (!searchResult) {
          return prev;
        }
        return produce(
          prev,
          (draft: {
            artifactCollection: {
              artifactMemberships: {edges: any[]; pageInfo: any};
            };
          }) => {
            if (
              draft.artifactCollection &&
              draft.artifactCollection.artifactMemberships.pageInfo
                .endCursor !== searchResult.pageInfo.endCursor
            ) {
              draft.artifactCollection.artifactMemberships.edges.push(
                ...searchResult.edges
              );
              draft.artifactCollection.artifactMemberships.pageInfo =
                searchResult.pageInfo;
              const {tableData, historyKeys, runHistoryError, metadataError} =
                parseArtifactVersionsTableData(fetchMoreResult);
              setArtifactVersionsTableData(edges => edges.concat(tableData));
              setHistoryKeys(prev => new Set([...prev, ...historyKeys]));
              if (runHistoryError) {
                toast.error(
                  'Something went wrong parsing run history for some artifact versions, see console for details.'
                );
              }
              if (metadataError) {
                toast.error(
                  'Something went wrong parsing metadata for some artifact versions, see console for details.'
                );
              }
            }
          }
        );
      },
    });
  }, [artifactCollectionID, pageSize, fetchMore, data]);

  const metadataKeyTypes = useMemo(() => {
    const latestVersion =
      data?.artifactCollection?.artifactMemberships.edges?.[0]?.node?.artifact;
    if (!latestVersion) {
      return {};
    }
    try {
      let parsedMetadata = JSON.parse(latestVersion.metadata);
      parsedMetadata = filterMetadataKeys(parsedMetadata);
      return typedDictPropertyTypes(wandbJsonType(parsedMetadata));
    } catch (e) {
      console.error({e});
      return {};
    }
  }, [data]);

  return {
    data: artifactVersionsTableData,
    historyKeys: Array.from(historyKeys),
    metadataKeyTypes,
    loading: loading,
    fetchNextPage,
    hasNextPage:
      data?.artifactCollection?.artifactMemberships.pageInfo?.hasNextPage ??
      false,
    setPageSize: setPageSize,
    pageSize,
    refetch,
  };
};

function filterMetadataKeys(
  metadata: Record<string, any>
): Record<string, any> {
  const filteredMetadata: Record<string, any> = {};
  for (const key in metadata) {
    if (!key.startsWith('_')) {
      filteredMetadata[key] = metadata[key];
    }
  }
  return filteredMetadata;
}

// filter out non-numerical and system history keys
function getNumericalHistoryKeys(historyKeys: Record<string, any>) {
  return Object.entries(historyKeys)
    .filter(([key, value]) => {
      const isNumber = typeof value === 'number' && !isNaN(value);
      return !key.startsWith('_') && !isSystemMetric(key) && isNumber;
    })
    .map(([key]) => key);
}

export const useArtifactTags = (artifactCollectionID?: string) => {
  const {data, loading, error, refetch} = useArtifactTagsQuery({
    skip: !artifactCollectionID,
    variables: {artifactCollectionID: artifactCollectionID ?? ''},
    context: propagateErrorsContext(),
  });

  return {
    tags: mapGQLTags(
      data?.artifactCollection?.tags?.edges?.map(edge => edge.node) ?? []
    ),
    entityName: data?.artifactCollection?.project?.entity?.name ?? '',
    projectName: data?.artifactCollection?.project?.name ?? '',
    artifactCollectionName: data?.artifactCollection?.name ?? '',
    loading,
    error,
    refetch,
  };
};

export const useArtifactCollectionOverview = (
  artifactCollectionID?: string
) => {
  const {data, loading, error, refetch} = useArtifactCollectionOverviewQuery({
    skip: !artifactCollectionID,
    variables: {artifactCollectionID: artifactCollectionID ?? ''},
    context: propagateErrorsContext(),
    notifyOnNetworkStatusChange: true,
  });

  return {
    data: data
      ? {
          collectionName: data.artifactCollection?.name ?? '',
          entityName: data.artifactCollection?.project?.entity?.name ?? '',
          projectName: data.artifactCollection?.project?.name ?? '',
          projectId: data.artifactCollection?.project?.id ?? '',
          collectionId: data.artifactCollection?.id ?? '',
          collectionIsPortfolio:
            data.artifactCollection?.__typename === 'ArtifactPortfolio',
          artifactTypeName:
            data.artifactCollection?.defaultArtifactType?.name ?? '',
          lastMembershipVersionIndex:
            data.artifactCollection?.versionIndexes[0],
          versionIndexes: data.artifactCollection?.versionIndexes ?? [],
          aliases:
            data.artifactCollection?.aliases?.edges?.map(
              edge => edge.node?.alias
            ) ?? [],
        }
      : null,
    loading,
    error,
    refetch,
  };
};
